import { useEffect, useLayoutEffect, useRef } from "react";
import type { ReactNode } from "react";

interface SidePanelProps {
  /** Fired on ESC, backdrop click, or any other user-initiated dismiss. */
  onClose: () => void;
  /**
   * `id` of the element that names the panel — typically the heading inside
   * the consumer's header. Required so screen readers announce the title when
   * the dialog opens.
   */
  ariaLabelledBy: string;
  children: ReactNode;
  /** Optional test id forwarded to the dialog root. */
  testId?: string;
  /**
   * When true, suppresses the panel's default focus-restore-on-close behavior.
   * Used when the parent is closing this panel in the same React commit as
   * opening a successor surface (e.g. a Reassign modal triggered from a
   * roster row) — the modal's own initial focus handling owns focus from
   * that point and a queued `setTimeout` restore would otherwise steal focus
   * from the modal one tick later. Default `false`.
   */
  skipFocusRestore?: boolean;
}

/**
 * Right-anchored 400px drawer built on the native `<dialog>` element so we
 * get a11y semantics (focus trap, ESC-to-close, `aria-modal`) "for free".
 * Slide-in animation is driven by a `data-open` attribute toggled in a
 * layout effect once `showModal()` has run.
 *
 * Mount = open, unmount = closed. The previous `isOpen` prop was always
 * `true` from every consumer (parents conditionally mount the panel) so it
 * carried no information; removing it dodges the structural smell. Consumers
 * unmount the panel to dismiss; the panel calls `onClose` only on
 * user-initiated dismissal (ESC, backdrop click).
 *
 * Consumers own the panel chrome (header, close button, body) — the primitive
 * only provides the surface + backdrop + animation + a11y. Both
 * `EventDetailPanel` and `SectionRosterPanel` plug their own header in.
 */
export function SidePanel({
  onClose,
  ariaLabelledBy,
  children,
  testId,
  skipFocusRestore = false,
}: SidePanelProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  // Mirror the latest `skipFocusRestore` value in a ref so the (stable)
  // unmount cleanup reads the current value at fire time rather than the
  // value captured when the effect first ran. Synced in a layout effect so
  // we don't access `.current` during render.
  const skipFocusRestoreRef = useRef(skipFocusRestore);
  useLayoutEffect(() => {
    skipFocusRestoreRef.current = skipFocusRestore;
  }, [skipFocusRestore]);

  // Open the dialog on mount; close on unmount. Capture the previously-
  // focused element BEFORE showing so we can restore on close.
  //
  // Focus restoration is done HERE (inline in the cleanup) rather than via a
  // separate `close` event listener because React's effect cleanups run in
  // reverse order: a sibling listener-detach effect would remove the `close`
  // handler BEFORE this effect calls `dialog.close()`. Folding the focus
  // logic into this same effect dodges the ordering hazard.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    lastFocusedRef.current =
      (document.activeElement as HTMLElement | null) ?? null;
    dialog.showModal();
    return () => {
      if (dialog.open) {
        dialog.close();
      }
      // When the parent is opening a successor surface in the same commit, it
      // sets `skipFocusRestore=true`. Drop the captured target so we neither
      // restore focus (the modal handles it) NOR leave a stale ref behind.
      if (skipFocusRestoreRef.current) {
        lastFocusedRef.current = null;
        return;
      }
      window.setTimeout(() => {
        const prev = lastFocusedRef.current;
        if (prev && typeof prev.focus === "function") {
          prev.focus();
        }
        // Drop the captured target so the NEXT open captures a fresh trigger
        // (P2-FE-5 — a stale ref across re-opens would otherwise misfire).
        lastFocusedRef.current = null;
      }, 0);
    };
  }, []);

  // Toggle the `data-open` attribute one frame after `showModal()` so the
  // CSS transition runs (translate-x-full → translate-x-0).
  //
  // We mutate the DOM attribute directly here instead of via React state to
  // avoid a cascading render inside the open effect — the attribute is purely
  // a CSS hook, not a value React downstream reads.
  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    // Off-screen frame must commit before flipping to true so the
    // transition runs (rather than snapping).
    dialog.setAttribute("data-open", "false");
    const id = requestAnimationFrame(() => {
      dialog.setAttribute("data-open", "true");
    });
    return () => cancelAnimationFrame(id);
  }, []);

  // ESC handling. Native `<dialog>` fires a `cancel` event on ESC, but jsdom
  // doesn't route it reliably, and we want the same behaviour either way:
  // dismiss via the consumer's `onClose` so the parent unmounts the panel.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dialog.open) {
        e.preventDefault();
        onClose();
      }
    };
    dialog.addEventListener("cancel", handleCancel);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      dialog.removeEventListener("cancel", handleCancel);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  // Backdrop click — the dialog itself is the backdrop element (its child div
  // is the chrome). Clicking the dialog (not a descendant) means the click
  // landed outside the panel chrome.
  const handleBackdropClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === dialogRef.current) {
      onClose();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={ariaLabelledBy}
      aria-modal="true"
      data-testid={testId}
      data-open="false"
      onClick={handleBackdropClick}
      // Native `<dialog>` defaults to centered + has its own padding/margin;
      // reset all of that and anchor right-side. `m-0` clears the default
      // browser margin; `inset-y-0 right-0 left-auto` pins to the right edge.
      // `translate-x-full` is the off-screen starting state; the data-open
      // selector slides it in. `[&::backdrop]:bg-black/50` styles the
      // browser-rendered backdrop layer.
      className="fixed inset-y-0 right-0 left-auto m-0 h-full w-[400px] max-w-[92vw] border-0 bg-transparent p-0 text-text-soft transition-transform duration-[250ms] ease-out translate-x-full data-[open=true]:translate-x-0 [&::backdrop]:bg-black/50"
    >
      <div className="flex h-full w-full flex-col bg-surface border-l border-border-subtle">
        {children}
      </div>
    </dialog>
  );
}
